import {
    Box,
    Link,
    Slider,
    styled,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { vec2 } from 'gl-matrix';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import EditableNumericValue from '~/components/Overlay/EditableNumericValue';
import useDose from '~/hooks/patient/dose/useDose';
import useViewportFocus from '~/hooks/useViewportFocus';
import {
    currentDoseState,
    selectedDoseRangeState,
} from '~/state/atoms/dose/doseAtoms';
import DoseOriginLabel from './DoseOriginLabel';
import { OverlayLabel, OverlayRow } from './OverlayLabel';

export const calculateRelativeDoseInPercent = (
    maxDoseGy?: number,
    normalizationValueGy?: number
): number | undefined => {
    if (!maxDoseGy || !normalizationValueGy) {
        return undefined;
    }
    return (100 * maxDoseGy) / normalizationValueGy;
};

export const calculateColorwashStep = (
    relativeDoseInPercent?: number
): number => {
    return relativeDoseInPercent ? 1 / relativeDoseInPercent : 0.01;
};

export const generateDoseLevelLabel = (
    dose: { maxDoseGy: number; generated: boolean } | undefined,
    doseScale: number,
    relativeDoseInPercent?: number
): string | null => {
    if (!dose || !relativeDoseInPercent) {
        return null;
    }

    const maxDosePercentage = relativeDoseInPercent.toFixed(0);
    const maxDoseGy = dose.maxDoseGy.toFixed(1);
    const doseRescaledByValue =
        doseScale !== 1 ? (((doseScale - 1) / 1) * 100).toFixed() : null;

    return dose.generated &&
        doseRescaledByValue &&
        parseFloat(doseRescaledByValue) !== 0
        ? `Max: ${maxDosePercentage} % (${maxDoseGy} Gy, scaled by ${doseRescaledByValue} %)`
        : `Max: ${maxDosePercentage} % (${maxDoseGy} Gy)`;
};

export const relativeToAbsoluteDose = (
    value: number,
    maxDose: number
): string => (value * maxDose).toFixed(1);

/**
 * Convert UI percent-of-prescription input to the slider's normalised
 * 0..1-of-max-dose range. Inverse of the `value * relativeDoseInPercent`
 * formula used in the per-thumb value label.
 */
const percentToSliderValue = (
    pct: number,
    relativeDoseInPercent: number | undefined
): number => {
    return relativeDoseInPercent ? pct / relativeDoseInPercent : pct / 100;
};

/**
 * Round a normalised slider value to the nearest valid step. Prescription-
 * normalised doses tick at exactly 1 %, but a non-normalised dose has a
 * sub-percent step, so a typed integer percent may not fall on a step.
 */
const snapToStep = (sliderValue: number, step: number): number => {
    if (step <= 0) {
        return sliderValue;
    }
    return Math.round(sliderValue / step) * step;
};

const ColorwashSlider = styled(Slider)(({ theme }) => ({
    height: 200,
    width: 16,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    '& .MuiSlider-thumb': {
        border: '1px solid #fff',
        backgroundColor: `${theme.palette.primary.main}`,
        width: 20,
        height: 8,
        borderRadius: 0,
    },
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'transparent',
        padding: 0,
        left: '46px',
        top: '12px',
        // Allow the editable percent inside the value label to receive
        // clicks/keys without the thumb stealing the gesture.
        pointerEvents: 'auto',
    },
    '& .MuiSlider-mark': {
        background: 'transparent',
    },
    '& .MuiSlider-rail': {
        height: '100%',
        background:
            'linear-gradient(to bottom, red, orange, yellow, lightgreen, lightblue, blue)',
        opacity: 0.7,
        borderRadius: 0,
    },
    '& .MuiSlider-track': {
        background: 'transparent',
        border: `3px solid ${theme.palette.background.paper}`,
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
    },
}));

export const DoseColorwashSlider = () => {
    const theme = useTheme();
    const intl = useIntl();
    const { dose, setDoseRange } = useDose();
    const doseRange = useRecoilValue(selectedDoseRangeState);
    const currentDose = useRecoilValue(currentDoseState);
    const { focusOnPoint } = useViewportFocus();
    const doseScale = dose?.doseScale ?? 1.0;

    const maxDosePosition = currentDose?.doseMaxPosition ?? null;
    const maxDoseGyDisplay = currentDose
        ? currentDose.doseMaxGy * currentDose.doseScale
        : 0;

    const handleFocusOnMax = () => {
        if (maxDosePosition) {
            focusOnPoint(maxDosePosition);
        }
    };

    const relativeDoseInPercent = useMemo(
        () =>
            calculateRelativeDoseInPercent(
                dose?.maxDoseGy,
                dose?.normalizationValueGy
            ),
        [dose]
    );

    const colorwashStep = useMemo(
        () => calculateColorwashStep(relativeDoseInPercent),
        [relativeDoseInPercent]
    );

    const doseLevelLabel = useMemo(
        () => generateDoseLevelLabel(dose, doseScale, relativeDoseInPercent),
        [dose, relativeDoseInPercent, doseScale]
    );

    const onColorwashValueChange = (
        _e: Event,
        value: number[] | number
    ): void => {
        const [min, max] = value as number[];
        if (doseRange && max !== min) {
            setDoseRange(vec2.fromValues(min, max));
        }
    };

    /**
     * Convert a normalised slider value to its displayed % of prescription.
     * Mirrors the formula used in `valueLabelFormat` (and historically the
     * only readout the user had).
     */
    const sliderValueToPercent = useCallback(
        (sliderValue: number): number => {
            return relativeDoseInPercent
                ? sliderValue * relativeDoseInPercent
                : sliderValue * 100;
        },
        [relativeDoseInPercent]
    );

    const handleMinCommit = useCallback(
        (newPct: number): void => {
            if (!doseRange) {
                return;
            }
            const snapped = snapToStep(
                percentToSliderValue(newPct, relativeDoseInPercent),
                colorwashStep
            );
            // Reject if snapping pushed min ≥ max; EditableNumericValue
            // re-syncs from `value` so the input visibly reverts.
            if (snapped >= doseRange[1]) {
                return;
            }
            setDoseRange(vec2.fromValues(snapped, doseRange[1]));
        },
        [doseRange, relativeDoseInPercent, colorwashStep, setDoseRange]
    );

    const handleMaxCommit = useCallback(
        (newPct: number): void => {
            if (!doseRange) {
                return;
            }
            const snapped = snapToStep(
                percentToSliderValue(newPct, relativeDoseInPercent),
                colorwashStep
            );
            if (snapped <= doseRange[0]) {
                return;
            }
            setDoseRange(vec2.fromValues(doseRange[0], snapped));
        },
        [doseRange, relativeDoseInPercent, colorwashStep, setDoseRange]
    );

    const formatPercent = (pct: number): string => `${pct.toFixed(0)} %`;

    return (
        dose &&
        doseRange && (
            <Box display="grid" gap={2}>
                <OverlayRow ml={-1} flexDirection="column">
                    <DoseOriginLabel />
                    {doseLevelLabel && maxDosePosition ? (
                        <Tooltip
                            title={intl.formatMessage(
                                { id: 'dose.focusOnMaxDose' },
                                { value: maxDoseGyDisplay.toFixed(1) }
                            )}
                        >
                            <Link
                                component="button"
                                type="button"
                                onClick={handleFocusOnMax}
                                underline="hover"
                                color="inherit"
                                data-testid="dose-overlay-focus-max-button"
                                aria-label={intl.formatMessage(
                                    { id: 'dose.focusOnMaxDose' },
                                    { value: maxDoseGyDisplay.toFixed(1) }
                                )}
                                sx={{
                                    '&:hover': { color: 'primary.main' },
                                }}
                            >
                                <OverlayLabel>{doseLevelLabel}</OverlayLabel>
                            </Link>
                        </Tooltip>
                    ) : (
                        <OverlayLabel>{doseLevelLabel}</OverlayLabel>
                    )}
                    {dose?.maxDoseGy.toFixed(1) !==
                        dose?.normalizationValueGy.toFixed(1) && (
                        <OverlayLabel>
                            Normalized to 100%:{' '}
                            {dose?.normalizationValueGy.toFixed(1)} Gy
                        </OverlayLabel>
                    )}
                </OverlayRow>
                <ColorwashSlider
                    orientation="vertical"
                    value={[doseRange[0] || 0, doseRange[1] || 0]}
                    step={colorwashStep}
                    min={0}
                    max={1}
                    size="small"
                    onChange={onColorwashValueChange}
                    valueLabelDisplay="on"
                    data-testid="dose-colorwash-slider"
                    valueLabelFormat={(value, index) => {
                        const isMax = index === 1;
                        return (
                            <Box
                                p={0.5}
                                borderRadius={1}
                                display="flex"
                                flexDirection="column"
                                gap={theme.spacing(0.25)}
                                sx={theme.mixins.glassOverlay}
                                // Block pointer/keyboard events from
                                // bubbling into the slider thumb so clicks
                                // on the editable percent don't start a
                                // drag.
                                onPointerDown={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                            >
                                <EditableNumericValue
                                    value={Math.round(
                                        sliderValueToPercent(value)
                                    )}
                                    onCommit={
                                        isMax
                                            ? handleMaxCommit
                                            : handleMinCommit
                                    }
                                    formatDisplay={formatPercent}
                                    ariaLabel={
                                        isMax
                                            ? 'Dose colorwash maximum, percent of prescription'
                                            : 'Dose colorwash minimum, percent of prescription'
                                    }
                                    testId={
                                        isMax
                                            ? 'dose-colorwash-max-input'
                                            : 'dose-colorwash-min-input'
                                    }
                                    unitSuffix=" %"
                                />
                                <Typography fontSize={10} lineHeight={1}>
                                    {relativeToAbsoluteDose(
                                        value,
                                        dose.maxDoseGy
                                    )}{' '}
                                    Gy
                                </Typography>
                            </Box>
                        );
                    }}
                />
            </Box>
        )
    );
};

export default DoseColorwashSlider;
